import React from "react";
import { Link } from "react-router-dom";
import MaterialIcon from "../../../shared/components/MaterialIcon";
import { DashboardPageHeader } from "../../../shared/components/dashboard/DashboardBento";
import { useTranslation } from "../../../i18n";
import useClientAccountStatus from "../hooks/useClientAccountStatus";
import {
  useCurrentUser,
  getUserFirstName,
} from "../../../shared/components/UserProfileBadge";

const BASE = "/dashboard/client";

const PERKS = [
  { key: "websiteDev", icon: "language" },
  { key: "crmDev", icon: "hub" },
  { key: "automation", icon: "bolt" },
  { key: "design", icon: "auto_awesome" },
];

const PerkCard = ({ icon, title, description, cta, discount }) => (
  <div className="tonal-card flex flex-col gap-4 rounded-xl border border-outline-variant/30 p-6">
    <div className="flex items-start justify-between">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary-container/40 text-secondary">
        <MaterialIcon name={icon} className="text-[24px]" />
      </div>
      <span className="rounded-full bg-secondary-container px-3 py-1 font-label-sm font-semibold text-on-secondary-container">
        {discount}
      </span>
    </div>
    <div>
      <h3 className="font-headline-md text-headline-md text-on-surface">{title}</h3>
      <p className="mt-1 font-body-md text-on-surface-variant">{description}</p>
    </div>
    <Link
      to={`${BASE}/support`}
      className="mt-auto inline-flex items-center gap-1 font-label-sm font-semibold text-secondary hover:underline"
    >
      {cta}
      <MaterialIcon name="arrow_forward" className="text-[16px]" />
    </Link>
  </div>
);

const ClientDiscountsPage = () => {
  const { t } = useTranslation();
  const { isPremium: isPaid, loading } = useClientAccountStatus();
  const currentUser = useCurrentUser();
  const firstName = getUserFirstName(currentUser);

  return (
    <div className="bg-background font-body-md text-on-surface antialiased">
      <div className="mx-auto min-h-screen max-w-container-max px-margin-mobile py-stack-lg md:px-margin-desktop">
        <DashboardPageHeader
          eyebrow={t("perks.eyebrow")}
          title={t("perks.title")}
          subtitle={t("perks.subtitle")}
          actions={
            isPaid ? (
              <span className="inline-flex items-center gap-2 rounded-full border border-outline-variant/40 bg-surface-container-lowest px-4 py-2 font-label-sm font-medium text-on-surface-variant">
                <MaterialIcon name="verified" className="text-[16px] text-secondary" />
                {t("perks.heroBadge")}
              </span>
            ) : null
          }
        />

        {loading && !isPaid ? null : isPaid ? (
          <>
            <div className="architect-gradient mb-stack-lg overflow-hidden rounded-2xl p-stack-lg text-white shadow-soft">
              <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/10 text-secondary-fixed">
                    <MaterialIcon name="verified" className="text-[26px]" />
                  </div>
                  <div>
                    <h2 className="font-headline-md text-headline-md text-white">
                      {firstName
                        ? t("perks.heroTitleNamed", { name: firstName })
                        : t("perks.heroTitle")}
                    </h2>
                    <p className="mt-1 max-w-2xl font-body-md text-white/70">
                      {t("perks.heroDesc")}
                    </p>
                  </div>
                </div>
                <div className="shrink-0 rounded-2xl bg-white/10 px-6 py-4 text-center">
                  <p className="font-headline-xl text-[32px] font-bold leading-none text-secondary-fixed">
                    {t("perks.discountBadge")}
                  </p>
                  <p className="mt-1 font-label-caps text-label-caps uppercase tracking-[0.14em] text-white/60">
                    {t("perks.heroBadge")}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-gutter md:grid-cols-2">
              {PERKS.map(({ key, icon }) => (
                <PerkCard
                  key={key}
                  icon={icon}
                  title={t(`perks.${key}Title`)}
                  description={t(`perks.${key}Desc`)}
                  cta={t("perks.claimCta")}
                  discount={t("perks.discountBadge")}
                />
              ))}
            </div>

            <div className="mt-stack-lg flex items-start gap-3 rounded-xl border border-outline-variant/30 bg-surface-container-lowest p-5">
              <MaterialIcon
                name="info"
                className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant"
              />
              <p className="font-body-md text-on-surface-variant">{t("perks.note")}</p>
            </div>
          </>
        ) : (
          <div className="tonal-card mx-auto max-w-2xl rounded-2xl border border-outline-variant/30 p-stack-lg text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-container text-on-surface-variant">
              <MaterialIcon name="lock" className="text-[26px]" />
            </div>
            <h2 className="font-headline-md text-headline-md text-on-surface">
              {t("perks.lockedTitle")}
            </h2>
            <p className="mx-auto mt-2 max-w-md font-body-md text-on-surface-variant">
              {t("perks.lockedDesc")}
            </p>
            <Link
              to={`${BASE}/billing`}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-5 py-3 font-label-sm font-semibold text-on-primary transition hover:bg-primary/90"
            >
              {t("perks.lockedCta")}
              <MaterialIcon name="arrow_forward" className="text-[18px]" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDiscountsPage;
